



































































import type { SelectEntity as ISelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import { ParagraphSize } from '@getaccept/lib-ui';
import type { AxiosError } from 'axios';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import type { Ref } from '@vue/composition-api';
import { onMounted, computed, defineComponent, ref } from '@vue/composition-api';
import { getAuthUrl } from '@getaccept/lib-shared/src/select-entity/select-entity.helper';
import type { ErrorMessage } from '../../types/error-message';
import { SelectEntityService } from '../../api/select-entities/services/select-entities.service';
import router from '../../router/router';
import { getErrorMessage } from '../../helpers/error-message';
import { ErrorKey } from '../../types/enums/error-key';
import { UserService } from '../../api/user/user.service';

export default defineComponent({
  setup() {
    const selectedEntityId = ref(null);
    const searchInput = ref('');
    const loadingEntities = ref(false);
    const loadingEntity = ref(false);
    const entities: Ref<ISelectEntity[]> = ref([]);
    const error: Ref<ErrorMessage> = ref(null);
    let userId = '';
    let goUrl = '';

    const showSearch = computed(() => entities.value?.length > 3);
    const createEntityUrl = computed(() => `/create-entity/${userId}`);
    const filteredEntities = computed(() =>
      searchInput.value
        ? entities.value.filter((entity: ISelectEntity) =>
            [entity.name].some((queryableProperty: string) =>
              queryableProperty.toLowerCase().includes(searchInput.value.toLowerCase())
            )
          )
        : entities.value
    );

    const loadEntities = async () => {
      try {
        loadingEntities.value = true;
        entities.value = await SelectEntityService.entities();
      } catch (e) {
        loadEntitiesFail(e);
      } finally {
        loadingEntities.value = false;
      }
    };

    const loadEntitiesFail = async (e: AxiosError) => {
      if (e?.request?.status === 401) {
        if (goUrl) {
          router.push({ name: 'login', query: { go: goUrl } });
          return;
        }
        router.push({ name: 'login' });
        return;
      }

      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      error.value = getErrorMessage(ErrorKey.Unknown);
    };

    const loadEntity = async (entityId: string) => {
      loadingEntity.value = true;
      try {
        await SelectEntityService.selectEntity(entityId);
        setTimeout(() => {
          window.open(goUrl || '/', '_self');
        }, 1300);
      } catch (e) {
        loadingEntity.value = false;
        if (bugsnagClient) {
          bugsnagClient.notify(e);
        }
        error.value = getErrorMessage(ErrorKey.LoadEntity);
      }
    };

    const authEntity = async ({ id, auth }: ISelectEntity) => {
      const go: string = goUrl ? `?go=${goUrl}` : '';
      const authUrl = getAuthUrl(id, auth);
      window.open(`${authUrl}${go}`, '_self');
    };

    const loadUserId = async () => {
      try {
        userId = await UserService.loadUserId();
      } catch (e) {
        if (bugsnagClient) {
          bugsnagClient.notify(e);
        }
        error.value = getErrorMessage(ErrorKey.FetchIdFailed);
      }
    };

    const checked = (id: string): boolean => {
      return selectedEntityId.value === id;
    };

    const setGoUrl = () => {
      goUrl = `${router.currentRoute.query.go || ''}`;
    };

    const selectEntity = (id: string) => {
      selectedEntityId.value = id;
    };

    const selectAndSubmit = (id: string) => {
      selectedEntityId.value = id;
      submit();
    };

    const submit = () => {
      const selectedEntity = entities.value.find(
        (entity: ISelectEntity) => entity.id === selectedEntityId.value
      );
      if (selectedEntity.auth) {
        authEntity(selectedEntity);
        return;
      }
      loadEntity(selectedEntityId.value);
    };

    onMounted(() => {
      setGoUrl();
      loadEntities();
      loadUserId();
    });

    return {
      selectedEntityId,
      searchInput,
      loadingEntities,
      loadingEntity,
      error,
      showSearch,
      filteredEntities,
      createEntityUrl,
      authEntity,
      loadEntity,
      checked,
      selectEntity,
      selectAndSubmit,
      ParagraphSize,
      submit,
      entities,
    };
  },
});
